import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import * as XLSX from 'xlsx';
import { Loader2, RefreshCw, AlertTriangle, ChevronDown, ChevronRight, ImageOff, Download } from 'lucide-react';
import MultiSelectDropdown from '../../../shared/components/MultiSelectDropdown';

/**
 * Div60AtsReport — CHA-369 stage 2, filters/export parity in CHA-373. The ChainTo twin of
 * `vw_div60_ats` (Power BI paginated linesheet reads the same view): an ATS report WITH product
 * images. Size-grain rows from getDiv60Ats are grouped to style×color×dim cards (dim keeps e.g.
 * C/B vs DD/D bra curves apart, matching the sales linesheet); a row expands to its sizes.
 * Filters mirror the paginated report: multi-select Season / Program / Group (MDSE) / Warehouse /
 * Category (empty selection = all), plus a Min-ATS threshold on the grouped total. Export writes
 * the filtered result as XLSX (Linesheet sheet + size-grain Sizes sheet) with a blank
 * DISCOUNT PRICE column for sales to fill and image URLs (browser export can't embed images —
 * that's the paginated report's job).
 */

const money = (v) => (v == null ? '—' : `$${Math.round(Number(v)).toLocaleString()}`);
const n = (x) => { const v = Number(x); return Number.isFinite(v) ? v : 0; };

export default function Div60AtsReport() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [seasonSel, setSeasonSel] = useState([]);
  const [programSel, setProgramSel] = useState([]);
  const [groupSel, setGroupSel] = useState([]);
  const [warehouseSel, setWarehouseSel] = useState([]);
  const [categorySel, setCategorySel] = useState([]);
  const [minAts, setMinAts] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState({}); // style|color|dim -> expanded

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'getDiv60Ats', { timeout: 150000 });
      const res = await fn({});
      setRows(res.data?.rows || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const distinct = useCallback((key) => Array.from(new Set((rows || []).map((r) => r[key]).filter(Boolean))).sort(), [rows]);
  const seasons = useMemo(() => distinct('season').reverse(), [distinct]);
  const programs = useMemo(() => distinct('program'), [distinct]);
  const mdseGroups = useMemo(() => distinct('mdse'), [distinct]);
  const warehouses = useMemo(() => distinct('warehouse'), [distinct]);
  const categories = useMemo(() => distinct('category'), [distinct]);

  const groups = useMemo(() => {
    const q = search.trim().toUpperCase();
    const m = new Map();
    for (const r of rows || []) {
      if (seasonSel.length && !seasonSel.includes(r.season)) continue;
      if (programSel.length && !programSel.includes(r.program)) continue;
      if (groupSel.length && !groupSel.includes(r.mdse)) continue;
      if (warehouseSel.length && !warehouseSel.includes(r.warehouse)) continue;
      if (categorySel.length && !categorySel.includes(r.category)) continue;
      if (q && !`${r.style} ${r.style_name || ''} ${r.color || ''} ${r.upc || ''}`.toUpperCase().includes(q)) continue;
      const key = `${r.style}|${r.color}|${r.dim || ''}`;
      if (!m.has(key)) {
        m.set(key, { key, style: r.style, color: r.color, dim: r.dim, style_name: r.style_name, season: r.season, program: r.program, category: r.category, mdse: r.mdse, image_url: r.image_url, a_price: null, ret_price: null, ats: 0, qoh: 0, wip: 0, open_qty: 0, value: 0, sizes: [] });
      }
      const g = m.get(key);
      if (!g.image_url && r.image_url) g.image_url = r.image_url;
      const ap = Number(r.a_price); const rp = Number(r.ret_price);
      if (r.a_price != null && Number.isFinite(ap) && (g.a_price == null || ap > g.a_price)) g.a_price = ap;
      if (r.ret_price != null && Number.isFinite(rp) && (g.ret_price == null || rp > g.ret_price)) g.ret_price = rp;
      g.ats += n(r.ats); g.qoh += n(r.qoh); g.wip += n(r.wip_qty); g.open_qty += n(r.open_qty); g.value += n(r.qoh_value);
      g.sizes.push(r);
    }
    const min = Number(minAts);
    const list = Array.from(m.values()).filter((g) => !(Number.isFinite(min) && min > 0) || g.ats >= min);
    return list.sort((a, b) => b.ats - a.ats || b.qoh - a.qoh);
  }, [rows, seasonSel, programSel, groupSel, warehouseSel, categorySel, minAts, search]);

  const totals = useMemo(() => groups.reduce((t, g) => ({ ats: t.ats + g.ats, qoh: t.qoh + g.qoh, wip: t.wip + g.wip, value: t.value + g.value }), { ats: 0, qoh: 0, wip: 0, value: 0 }), [groups]);

  const exportXlsx = useCallback(() => {
    const linesheet = groups.map((g) => {
      const bySize = {};
      for (const s of g.sizes) { const k = s.size || s.size_code || '?'; bySize[k] = (bySize[k] || 0) + n(s.ats); }
      return {
        Style: g.style, 'Style Name': g.style_name || '', Group: g.mdse || '', Color: g.color || '', 'Dm/Pk': g.dim || '',
        Season: g.season || '', Program: g.program || '', Category: g.category || '',
        'DISCOUNT PRICE': '', 'WH PRICE': g.a_price ?? '', MSRP: g.ret_price ?? '',
        'ATS Qty': g.ats, QOH: g.qoh, WIP: g.wip, 'Open Qty': g.open_qty, 'QOH Value': Math.round(g.value),
        Sizes: Object.entries(bySize).map(([k, v]) => `${k}:${v}`).join('  '),
        'Image URL': g.image_url || '',
      };
    });
    const sizeRows = groups.flatMap((g) => g.sizes.map((s) => ({
      Style: s.style, 'Style Name': s.style_name || '', Group: s.mdse || '', Color: s.color || '', 'Dm/Pk': s.dim || '',
      Size: s.size || s.size_code || '', UPC: s.upc || '', Season: s.season || '', Warehouse: s.warehouse || '',
      ATS: n(s.ats), QOH: n(s.qoh), WIP: n(s.wip_qty), 'In Transit': n(s.wip_intransit_qty), Open: n(s.open_qty), Pick: n(s.pick_qty),
      'WH PRICE': s.a_price ?? '', MSRP: s.ret_price ?? '',
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(linesheet), 'Linesheet');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sizeRows), 'Sizes');
    XLSX.writeFile(wb, `div60_ats_linesheet_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }, [groups]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <MultiSelectDropdown label="Seasons" options={seasons} selected={seasonSel} onChange={setSeasonSel} />
        <MultiSelectDropdown label="Programs" options={programs} selected={programSel} onChange={setProgramSel} />
        <MultiSelectDropdown label="Groups" options={mdseGroups} selected={groupSel} onChange={setGroupSel} />
        <MultiSelectDropdown label="Warehouses" options={warehouses} selected={warehouseSel} onChange={setWarehouseSel} />
        <MultiSelectDropdown label="Categories" options={categories} selected={categorySel} onChange={setCategorySel} />
        <input type="number" min="0" value={minAts} onChange={(e) => setMinAts(e.target.value)} placeholder="Min ATS"
          className="w-24 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500" />
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search style / name / color / UPC…"
          className="w-64 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500" />
        <span className="text-xs text-slate-500 ml-auto">
          {groups.length.toLocaleString()} style·colors · ATS {totals.ats.toLocaleString()} · QOH {totals.qoh.toLocaleString()} · WIP {totals.wip.toLocaleString()} · {money(totals.value)}
        </span>
        <button onClick={exportXlsx} disabled={!groups.length} title="Export filtered linesheet to Excel"
          className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 text-sm font-medium px-2.5 py-1 rounded">
          <Download className="h-4 w-4" />Export
        </button>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 text-sm font-medium px-2.5 py-1 rounded">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </button>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{error}</div>}
      {loading && !rows && <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading Div 60 ATS…</div>}

      {rows && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="text-slate-500 text-xs bg-slate-50">
              <tr>{['', 'Image', 'Style', 'Name', 'Color', 'Dm/Pk', 'Season', 'Program', 'ATS', 'QOH', 'WIP', 'Open', 'QOH value'].map((h, i) => <th key={i} className="text-left font-medium px-3 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {groups.map((g) => (
                <React.Fragment key={g.key}>
                  <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => setOpen((o) => ({ ...o, [g.key]: !o[g.key] }))}>
                    <td className="px-2 py-1.5 text-slate-400">{open[g.key] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                    <td className="px-3 py-1.5">
                      {g.image_url ? (
                        <img src={g.image_url} alt={g.style} loading="lazy"
                          className="h-12 w-12 object-cover rounded border border-slate-200 bg-slate-50"
                          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }} />
                      ) : null}
                      <span className="h-12 w-12 rounded border border-slate-200 bg-slate-50 text-slate-300 items-center justify-center" style={{ display: g.image_url ? 'none' : 'flex' }}><ImageOff className="h-5 w-5" /></span>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs">{g.style}</td>
                    <td className="px-3 py-1.5 text-xs max-w-[16rem] truncate" title={g.style_name}>{g.style_name}</td>
                    <td className="px-3 py-1.5 text-xs">{g.color}</td>
                    <td className="px-3 py-1.5 text-xs text-slate-500">{g.dim}</td>
                    <td className="px-3 py-1.5 text-xs text-slate-500">{g.season}</td>
                    <td className="px-3 py-1.5 text-xs text-slate-500">{g.program}</td>
                    <td className="px-3 py-1.5 tabular-nums font-semibold text-teal-700">{g.ats.toLocaleString()}</td>
                    <td className="px-3 py-1.5 tabular-nums text-slate-600">{g.qoh.toLocaleString()}</td>
                    <td className="px-3 py-1.5 tabular-nums text-slate-600">{g.wip.toLocaleString()}</td>
                    <td className="px-3 py-1.5 tabular-nums text-slate-600">{g.open_qty.toLocaleString()}</td>
                    <td className="px-3 py-1.5 tabular-nums text-slate-600">{money(g.value)}</td>
                  </tr>
                  {open[g.key] && (
                    <tr>
                      <td colSpan={13} className="px-10 py-2 bg-slate-50/60">
                        <table className="text-xs min-w-[40rem]">
                          <thead className="text-slate-400">
                            <tr>{['Size', 'WH', 'ATS', 'QOH', 'WIP', 'In transit', 'Open', 'Pick', 'Price'].map((h, i) => <th key={i} className="text-left font-medium pr-4 py-1">{h}</th>)}</tr>
                          </thead>
                          <tbody>
                            {g.sizes.map((s, i) => (
                              <tr key={i}>
                                <td className="pr-4 py-0.5">{s.size || s.size_code}</td>
                                <td className="pr-4 py-0.5 text-slate-500">{s.warehouse}</td>
                                <td className="pr-4 py-0.5 tabular-nums">{n(s.ats).toLocaleString()}</td>
                                <td className="pr-4 py-0.5 tabular-nums">{n(s.qoh).toLocaleString()}</td>
                                <td className="pr-4 py-0.5 tabular-nums">{n(s.wip_qty).toLocaleString()}</td>
                                <td className="pr-4 py-0.5 tabular-nums">{n(s.wip_intransit_qty).toLocaleString()}</td>
                                <td className="pr-4 py-0.5 tabular-nums">{n(s.open_qty).toLocaleString()}</td>
                                <td className="pr-4 py-0.5 tabular-nums">{n(s.pick_qty).toLocaleString()}</td>
                                <td className="pr-4 py-0.5 tabular-nums">{s.a_price != null ? `$${Number(s.a_price).toFixed(2)}` : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {!groups.length && <div className="p-4 text-sm text-slate-500">No rows match the filters.</div>}
        </div>
      )}
    </div>
  );
}
